export const MAX_ADVANCED_BEAUTIFY_REGIONS = 8;

export interface AdvancedBeautifyRegion {
  id: string;
  number: number;
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface RegionInstruction {
  number: number;
  instruction: string;
}

export interface ImageContentRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

export function containImageRect(
  containerWidth: number,
  containerHeight: number,
  imageWidth: number,
  imageHeight: number,
): ImageContentRect {
  if (containerWidth <= 0 || containerHeight <= 0 || imageWidth <= 0 || imageHeight <= 0) {
    return { left: 0, top: 0, width: 0, height: 0 };
  }
  const scale = Math.min(containerWidth / imageWidth, containerHeight / imageHeight);
  const width = imageWidth * scale;
  const height = imageHeight * scale;
  return {
    left: (containerWidth - width) / 2,
    top: (containerHeight - height) / 2,
    width,
    height,
  };
}

export function pointInImageRect(
  clientX: number,
  clientY: number,
  elementRect: Pick<DOMRect, "left" | "top">,
  imageRect: ImageContentRect,
): { x: number; y: number } | undefined {
  if (!imageRect.width || !imageRect.height) return undefined;
  const x = clientX - elementRect.left - imageRect.left;
  const y = clientY - elementRect.top - imageRect.top;
  if (x < 0 || y < 0 || x > imageRect.width || y > imageRect.height) return undefined;
  return {
    x: Math.max(0, Math.min(1, x / imageRect.width)),
    y: Math.max(0, Math.min(1, y / imageRect.height)),
  };
}

export function regionFromPoints(
  start: { x: number; y: number },
  end: { x: number; y: number },
  id: string,
  number: number,
): AdvancedBeautifyRegion {
  const x = Math.max(0, Math.min(start.x, end.x));
  const y = Math.max(0, Math.min(start.y, end.y));
  const right = Math.min(1, Math.max(start.x, end.x));
  const bottom = Math.min(1, Math.max(start.y, end.y));
  return { id, number, x, y, width: right - x, height: bottom - y };
}

export function removeAndRenumberRegions(
  regions: readonly AdvancedBeautifyRegion[],
  removedId: string,
): AdvancedBeautifyRegion[] {
  return regions
    .filter((region) => region.id !== removedId)
    .map((region, index) => ({ ...region, number: index + 1 }));
}

export function appendRegionInstructionLine(value: string, number: number): string {
  const text = value.trimEnd();
  if (number === 1 && text && !containsRegionInstructionLine(text)) return `框1：${text.trim()}`;
  return `${text}${text ? "\n" : ""}框${number}：`;
}

export function removeRegionInstructionLine(value: string, removedNumber: number): string {
  let nextNumber = 1;
  return value
    .split(/\r?\n/)
    .flatMap((line) => {
      const match = /^\s*框(\d+)[：:]\s*(.*)$/.exec(line);
      if (!match) return [line];
      if (Number(match[1]) === removedNumber) return [];
      const result = `框${nextNumber}：${match[2]}`;
      nextNumber += 1;
      return [result];
    })
    .join("\n")
    .trim();
}

export function parseRegionInstructions(
  value: string,
  expectedCount: number,
): { ok: true; instructions: RegionInstruction[] } | { ok: false; error: string } {
  const parsed = new Map<number, string>();
  for (const [lineIndex, line] of value.split(/\r?\n/).entries()) {
    if (!line.trim()) continue;
    const match = /^\s*框(\d+)[：:]\s*(.*?)\s*$/.exec(line);
    if (!match) {
      const recognizableNumber = /^\s*(?:框\s*)?(\d+)/.exec(line);
      const number = Number(recognizableNumber?.[1]);
      if (Number.isInteger(number) && number >= 1 && number <= expectedCount) {
        return { ok: false, error: `框${number}格式不正确，请按“框${number}：修改要求”填写` };
      }
      return { ok: false, error: `第${lineIndex + 1}行格式不正确，请按“框X：修改要求”逐行填写` };
    }
    const number = Number(match[1]);
    if (!Number.isInteger(number) || number < 1 || number > expectedCount) {
      return {
        ok: false,
        error: `文字中出现框${match[1]}，但当前只有${expectedCount}个框，请检查画框编号`,
      };
    }
    if (parsed.has(number)) {
      return { ok: false, error: `框${number}重复填写，请只保留一条修改意见` };
    }
    if (!match[2]) return { ok: false, error: `请填写框${number}的修改要求` };
    parsed.set(number, match[2]);
  }
  const missingNumbers = Array.from(
    { length: expectedCount },
    (_, index) => index + 1,
  ).filter((number) => !parsed.has(number));
  if (missingNumbers.length === 1) {
    const number = missingNumbers[0];
    return { ok: false, error: `框${number}没有填写修改意见，请补充“框${number}：修改要求”` };
  }
  if (missingNumbers.length > 1) {
    return {
      ok: false,
      error: `框${missingNumbers.join("、框")}没有填写修改意见，请分别补充对应的“框X：修改要求”`,
    };
  }
  return {
    ok: true,
    instructions: Array.from({ length: expectedCount }, (_, index) => ({
      number: index + 1,
      instruction: parsed.get(index + 1) || "",
    })),
  };
}

function containsRegionInstructionLine(value: string): boolean {
  return value.split(/\r?\n/).some((line) => /^\s*框\d+[：:]/.test(line));
}
